import React, { useState, useEffect } from 'react';
import { Users, Wifi } from 'lucide-react';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useUserManager } from '../hooks/useUserManager';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';

export default function BroadcastButton({
  channel,
  isBroadcastActive,
  broadcastToken,
  onBroadcastStarted,
  onStopBroadcast,
  className
}) {
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const authenticatedFetch = useAuthenticatedFetch();
  const { users } = useUserManager();
  const { token } = useAuth();
  const { addNotification } = useNotifications();

  // Carica utenti connessi quando in broadcast
  useEffect(() => {
    if (!isBroadcastActive || !broadcastToken) {
      setConnectedUsers([]);
      setIsPolling(false);
      return;
    }

    setIsPolling(true);
    const pollUsers = async () => {
      try {
        const response = await authenticatedFetch(`/api/broadcast/users/${broadcastToken}`, { noLogoutOn401: true });
        if (response.ok) {
          const data = await response.json();
          setConnectedUsers(data.users || []);
        } else {
          setConnectedUsers([]);
        }
      } catch {
        setConnectedUsers([]);
      }
    };

    pollUsers();
    const interval = setInterval(pollUsers, 3000);
    return () => {
      clearInterval(interval);
      setIsPolling(false);
    };
  }, [isBroadcastActive, broadcastToken, authenticatedFetch]);

  // Carica utenti invitati quando in broadcast
  useEffect(() => {
    if (!isBroadcastActive || !broadcastToken) {
      setInvitedUsers([]);
      return;
    }

    const loadInvited = async () => {
      try {
        const res = await authenticatedFetch(`/api/broadcast/invited/${broadcastToken}`, { noLogoutOn401: true });
        if (res.ok) {
          const data = await res.json();
          setInvitedUsers(data.users || []);
        }
      } catch {
        setInvitedUsers([]);
      }
    };

    loadInvited();
    const id = setInterval(loadInvited, 5000);
    return () => clearInterval(id);
  }, [isBroadcastActive, broadcastToken, authenticatedFetch]);

  const toggleInvite = async (username) => {
    if (isBroadcastActive) {
      if (invitedUsers.includes(username)) {
        await authenticatedFetch(`/api/broadcast/invite/${broadcastToken}/${username}`, { method: 'DELETE' });
        setInvitedUsers(invitedUsers.filter(u => u !== username));
      } else {
        await authenticatedFetch(`/api/broadcast/invite/${broadcastToken}`, {
          method: 'POST',
          body: JSON.stringify({ users: [username] })
        });
        setInvitedUsers([...invitedUsers, username]);
      }
    } else {
      setInvitedUsers(prev => prev.includes(username) ? prev.filter(u => u !== username) : [...prev, username]);
    }
  };

  const startBroadcast = async () => {
    // 🔒 SICUREZZA: Usa SEMPRE l'URL proxy incapsulato, mai l'URL originale
    const proxyUrl = channel.url;

    console.log('🎬 [BROADCAST-FRONTEND] Starting broadcast:', {
      proxyUrl,
      channelName: channel.name,
      invitedUsers,
      protocol: window.location.protocol,
      host: window.location.host,
      origin: window.location.origin,
      isProxyUrl: proxyUrl.includes('/proxy/'),
      timestamp: new Date().toISOString()
    });

    try {
      const requestData = { 
        url: proxyUrl,  // 🔒 USA SEMPRE l'URL proxy incapsulato
        channelName: channel.name, 
        inviteUsers: invitedUsers 
      };
      
      console.log('🎬 [BROADCAST-FRONTEND] Sending request:', {
        endpoint: '/api/broadcast/start',
        method: 'POST',
        data: requestData,
        timestamp: new Date().toISOString()
      });

      const res = await authenticatedFetch('/api/broadcast/start', {
        method: 'POST',
        body: JSON.stringify(requestData)
      });

      console.log('🎬 [BROADCAST-FRONTEND] Response received:', {
        ok: res.ok,
        status: res.status,
        statusText: res.statusText,
        headers: Object.fromEntries(res.headers.entries()),
        timestamp: new Date().toISOString()
      });

      if (res.ok) {
        const data = await res.json();
        console.log('✅ [BROADCAST-FRONTEND] Broadcast started successfully:', {
          data,
          timestamp: new Date().toISOString()
        });

        const linkWithJwt = `${data.link}?jwt=${token}`;
        console.log('🎬 [BROADCAST-FRONTEND] Generated link with JWT:', {
          originalLink: data.link,
          linkWithJwt,
          token: token?.substring(0, 20) + '...',
          timestamp: new Date().toISOString()
        });

        onBroadcastStarted?.(data.token, linkWithJwt);
        setShowDropdown(false);
      } else {
        const errorText = await res.text();
        console.error('❌ [BROADCAST-FRONTEND] Broadcast start failed:', {
          status: res.status,
          statusText: res.statusText,
          errorText,
          timestamp: new Date().toISOString()
        });
        addNotification({ message: `Errore avvio broadcast: ${res.status}` });
      }
    } catch (error) {
      console.error('❌ [BROADCAST-FRONTEND] Broadcast start error:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      });
      addNotification({ message: 'Errore avvio broadcast' });
    }
  };

  const userList = users.filter(u => u.username !== 'admin');

  if (!isBroadcastActive) {
    return (
      <div className="relative">
        <button className={className} onClick={() => setShowDropdown(!showDropdown)}>
          <Wifi size={16} />
          <span className="hidden sm:inline">Broadcast</span>
        </button>
        {showDropdown && (
          <div className="absolute bottom-full mb-2 left-0 w-64 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl z-50">
            <div className="p-3 border-b border-gray-700">
              <h4 className="text-white font-medium">Invita Utenti</h4>
            </div>
            <div className="max-h-40 overflow-y-auto">
              {userList.map(u => (
                <label key={u.id} className="flex items-center gap-2 p-2 border-b border-gray-800 last:border-b-0">
                  <input type="checkbox" checked={invitedUsers.includes(u.username)} onChange={() => toggleInvite(u.username)} />
                  <span className="text-white text-sm">{u.username}</span>
                </label>
              ))}
            </div>
            <div className="p-2 border-t border-gray-700">
              <button onClick={startBroadcast} className="w-full bg-green-600 hover:bg-green-700 text-white text-sm rounded px-3 py-1 transition-colors">
                Start Broadcast
              </button>
            </div>
          </div>
        )}
        {showDropdown && <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)} />}
      </div>
    );
  }

  return (
    <div className="relative">
      <button className={`${className} relative`} onClick={() => setShowDropdown(!showDropdown)}>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Wifi size={16} className="text-green-400" />
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          </div>
          <span className="hidden sm:inline">Live ({connectedUsers.length})</span>
          <span className="sm:hidden bg-green-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {connectedUsers.length}
          </span>
        </div>
      </button>
      {showDropdown && (
        <div className="absolute bottom-full mb-2 left-0 w-72 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl z-50">
          <div className="p-3 border-b border-gray-700">
            <h4 className="text-white font-medium flex items-center gap-2">
              <Users size={16} className="text-green-400" />
              Utenti Connessi ({connectedUsers.length})
            </h4>
          </div>
          <div className="max-h-32 overflow-y-auto">
            {connectedUsers.length > 0 ? (
              connectedUsers.map((u, i) => (
                <div key={i} className="p-2 border-b border-gray-800 last:border-b-0 text-white text-sm">
                  {u || 'Utente Anonimo'}
                </div>
              ))
            ) : (
              <div className="p-3 text-gray-400 text-sm text-center">Nessun utente connesso</div>
            )}
          </div>
          {isPolling && (
            <div className="p-2 bg-gray-800/50 text-center">
              <span className="text-gray-400 text-xs">Aggiornamento in tempo reale...</span>
            </div>
          )}
          <div className="p-3 border-t border-gray-700">
            <h4 className="text-white font-medium mb-2">Gestisci Inviti</h4>
            <div className="max-h-32 overflow-y-auto mb-2">
              {userList.map(u => (
                <label key={u.id} className="flex items-center gap-2 p-1">
                  <input type="checkbox" checked={invitedUsers.includes(u.username)} onChange={() => toggleInvite(u.username)} />
                  <span className="text-white text-sm">{u.username}</span>
                </label>
              ))}
            </div>
            <button onClick={() => { setShowDropdown(false); onStopBroadcast?.(); }} className="w-full bg-red-600 hover:bg-red-700 text-white text-sm rounded px-3 py-1 transition-colors">
              Stop Broadcast
            </button>
          </div>
        </div>
      )}
      {showDropdown && <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)} />}
    </div>
  );
}
